import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationsService } from '../../core/services/notifications.service';
import { notificationApiActions } from '../actions/notifications.action';
import { selectUserBenId } from '../selectors/authentication.selectors';
import { selectNotification } from '../selectors/notifications.selector';

@Injectable()
export class NotificationsEffects {
  constructor(private actions$: Actions, private service: NotificationsService, private store: Store, private router: Router) { }

  // Cantidad de no leídas para el badge de Avisos: se cuentan las de la primera página.
  unreadCount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationApiActions.unreadCountRequest),
      switchMap(({ benId }) =>
        this.service.getNotification(benId, 1).pipe(
          map(({ data }) => notificationApiActions.unreadCountSuccess({
            unreadCount: (data ?? []).filter(notification => !notification.read).length,
          })),
          catchError(() => of(notificationApiActions.unreadCountFailure({ error: 'Error on unread count' })))
        )
      )
    );
  });

  // Push con la pestaña abierta: en Notificaciones se refresca el listado (en su página actual);
  // en el resto solo el contador. El contador sale del listado cuando se pide la página 1.
  pushReceived$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationApiActions.pushReceived),
      withLatestFrom(this.store.select(selectUserBenId), this.store.select(selectNotification)),
      filter(([, benId]) => !!benId),
      mergeMap(([, benId, notifications]) => {
        const url = this.router.url;
        if (url.startsWith('/dashboard/notificaciones')) {
          const page = notifications?.currentPage ?? 1;
          return [
            notificationApiActions.notificationsRefresh({ benId, page }),
            ...(page === 1 ? [] : [notificationApiActions.unreadCountRequest({ benId })]),
          ];
        }
        return [notificationApiActions.unreadCountRequest({ benId })];
      })
    );
  });

  notifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationApiActions.notificationsRequest.type, notificationApiActions.notificationsRefresh.type),
      switchMap((action: Action & { benId: number, page: number }) =>
        this.service.getNotification(action.benId, action.page).pipe(
          map(payload => notificationApiActions.notificationsSuccess({ notifications: payload })),
          catchError(() => of(notificationApiActions.notificationsFailure({ error: 'Error on plan' })))
        )
      )
    );
  });
  
  notificationsAuthorizations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationApiActions.notificationsAuthorizationsRequest.type),
      switchMap((action: Action & { benId: number }) =>
        this.service.getNotificationAuthorizations(action.benId).pipe(
          map(payload => notificationApiActions.notificationsAuthorizationsSuccess({ notifications: payload })),
          catchError(() => of(notificationApiActions.notificationsAuthorizationsFailure({ error: 'Error on plan' })))
        )
      )
    );
  });

  notificationDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationApiActions.notificationDetailRequest),
      switchMap(({ id }) =>
        this.service.getNotificationDetail(id).pipe(
          map(notification => notificationApiActions.notificationDetailSuccess({ notification })),
          catchError(() => of(notificationApiActions.notificationDetailFailure({ error: 'Error on notification detail' })))
        )
      )
    );
  });

  notificationRead$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationApiActions.notificationReadRequest),
      mergeMap(({ benId, id }) =>
        this.service.markNotificationAsRead(benId, id).pipe(
          map(() => notificationApiActions.notificationReadSuccess({ id })),
          catchError(() => of(notificationApiActions.notificationReadFailure({ error: 'Error on notification read' })))
        )
      )
    );
  });
}
